import { CheckCircle2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FeatureGuard } from '@/hooks/use-plan-features';
import { UPCOMING_DAYS } from '@/lib/dueWindow';

// Abas Todas / Vencidas / Próximos N dias e o aviso de aba vazia — usados por
// Despesas Mensais e Receitas (mesma lógica nas duas páginas).

export type DueTab = 'all' | 'overdue' | 'upcoming';

// Contagem ainda não carregada (undefined) não mostra número — nada de um "0"
// enganoso enquanto a lista chega.
interface DueFilterTabsListProps {
  allCount?: number;
  overdueCount?: number;
  upcomingCount?: number;
  /** Funcionalidade do plano que libera a aba "Vencidas" (sem ela, a aba é livre). */
  overdueFeature?: string;
}

/** Lista de abas (vai dentro de `<Tabs>`); os valores são os de `DueTab`. */
export function DueFilterTabsList({
  allCount,
  overdueCount,
  upcomingCount,
  overdueFeature,
}: Readonly<DueFilterTabsListProps>) {
  const overdueTab = (
    <TabsTrigger value="overdue" className="text-xs sm:text-sm">
      Vencidas
      {!!overdueCount && (
        <Badge variant="destructive" className="ml-1 sm:ml-2 text-[10px] sm:text-xs">
          {overdueCount}
        </Badge>
      )}
    </TabsTrigger>
  );

  return (
    <div className="flex items-center justify-between overflow-x-auto">
      <TabsList className="w-full sm:w-auto">
        <TabsTrigger value="all" className="text-xs sm:text-sm">
          Todas
          {allCount !== undefined && (
            <Badge variant="secondary" className="ml-1 sm:ml-2 text-[10px] sm:text-xs">
              {allCount}
            </Badge>
          )}
        </TabsTrigger>
        {overdueFeature ? (
          <FeatureGuard feature={overdueFeature} fallback={null}>
            {overdueTab}
          </FeatureGuard>
        ) : (
          overdueTab
        )}
        <TabsTrigger value="upcoming" className="text-xs sm:text-sm">
          <span className="hidden sm:inline">Próximos {UPCOMING_DAYS} dias</span>
          <span className="sm:hidden">{UPCOMING_DAYS} dias</span>
          {upcomingCount !== undefined && (
            <Badge variant="secondary" className="ml-1 sm:ml-2 text-[10px] sm:text-xs">
              {upcomingCount}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>
    </div>
  );
}

interface DueEmptyStateProps {
  title: string;
  message: string;
  backLabel: string;
  onBack: () => void;
}

/** Aviso de aba vazia com o botão de voltar para "Todas". */
export function DueEmptyState({ title, message, backLabel, onBack }: Readonly<DueEmptyStateProps>) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <CheckCircle2 className="h-12 w-12 text-emerald-500/30 mb-4" />
        <h3 className="text-lg font-medium mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button className="mt-4 bg-emerald-600 text-white hover:bg-emerald-700" onClick={onBack}>
          {backLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
